import type { WorkBook } from 'xlsx';

export const IMPORT_BATCH_SIZE = 500;

export const importFields = ['name', 'barcode', 'firm', 'model', 'size', 'description', 'price', 'quantity'] as const;
export type ImportField = typeof importFields[number];
export type Mapping = Partial<Record<ImportField, number>>;
export interface ImportRow { row: number; name: string; barcode: string; firm: string; model: string; size: string; description: string; price: string; quantity: string }
const aliases: Record<ImportField, string[]> = {
  name: ['name', 'product', 'product name', 'item', 'article', 'denumire', 'produs', 'denumire produs', 'название', 'товар', 'наименование'],
  barcode: ['barcode', 'ean', 'ean13', 'ean 13', 'cod de bare', 'cod bare', 'штрихкод', 'штрих код'],
  firm: ['firm', 'brand', 'manufacturer', 'marca', 'producator', 'бренд', 'производитель'],
  model: ['model', 'model number', 'модель'], size: ['size', 'marime', 'размер'],
  description: ['description', 'descriere', 'описание'], price: ['price', 'unit price', 'pret', 'pret unitar', 'цена'],
  quantity: ['quantity', 'qty', 'units', 'stock', 'cantitate', 'stoc', 'количество', 'остаток'],
};
const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[_-]/g, ' ').trim();
export function suggestMapping(headers: string[]): Mapping {
  const mapping: Mapping = {};
  importFields.forEach(field => {
    const matches = headers.map((header, index) => aliases[field].includes(normalize(header)) ? index : -1).filter(index => index >= 0);
    if (matches.length === 1) mapping[field] = matches[0];
  });
  return mapping;
}
export async function loadSpreadsheet(file: File): Promise<WorkBook> {
  if (!/\.(xlsx|csv)$/i.test(file.name) || file.size > 5 * 1024 * 1024) throw new Error('fileLimit');
  const XLSX = await import('xlsx');
  const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', cellFormula: true, sheetRows: 10002, raw: true });
  if (!workbook.SheetNames.length || workbook.SheetNames.length > 20) throw new Error('sheetLimit');
  return workbook;
}
export async function readTable(workbook: WorkBook, sheet: string, headerRow: number) {
  const XLSX = await import('xlsx');
  const worksheet = workbook.Sheets[sheet];
  if (!worksheet || !Number.isInteger(headerRow) || headerRow < 1 || headerRow > 20) throw new Error('headerLimit');
  const range = XLSX.utils.decode_range(worksheet['!fullref'] || worksheet['!ref'] || 'A1');
  if (range.e.c >= 40 || range.e.r >= 10000) throw new Error('sheetLimit');
  const rows: { row: number; cells: string[] }[] = [];
  for (let r = headerRow - 1; r <= range.e.r; r++) {
    const cells: string[] = [];
    for (let c = 0; c <= range.e.c; c++) {
      const cell = worksheet[XLSX.utils.encode_cell({ r, c })];
      if (cell?.f || cell?.t === 'e') throw new Error('formula');
      const value = cell ? XLSX.utils.format_cell(cell).trim() : '';
      if (value.length > 255) throw new Error('cellLimit');
      cells.push(value);
    }
    if (r === headerRow - 1 || cells.some(Boolean)) rows.push({ row: r + 1, cells });
  }
  const headers = rows.shift()?.cells || [];
  if (!headers.some(Boolean)) throw new Error('missingHeaders');
  if (!rows.length) throw new Error('missingData');
  return { headers, rows };
}
export function mapRows(rows: { row: number; cells: string[] }[], mapping: Mapping): ImportRow[] {
  return rows.map(row => {
    const result: ImportRow = { row: row.row, name: '', barcode: '', firm: '', model: '', size: '', description: '', price: '', quantity: '' };
    importFields.forEach(field => { const column = mapping[field]; result[field] = column === undefined ? '' : row.cells[column] || ''; });
    return result;
  });
}
export function csvCell(value: unknown) {
  const text = String(value ?? '');
  return '"' + (/^[\s]*[=+@-]/.test(text) ? "'" : '') + text.replace(/"/g, '""') + '"';
}
